import { useCallback, useState } from 'react';
import {
    Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
    Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getRegistrations, decideRegistration } from '../../api/eventApi';

const PP = "'Poppins', sans-serif";

const headSx = {
    fontFamily: PP, fontWeight: 700, fontSize: '0.72rem', color: '#475569',
    bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap',
};
const cellSx = { fontFamily: PP, fontSize: '0.8rem', color: '#1E293B' };

const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-MY', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' })
    : '—';

/* ============================================================================
   ATTENDANCE — one collapsible row per date

   A weekly series runs to a dozen dates, and a wall of date chips made you hunt
   for the one you wanted. A list of dates with their numbers reads at a glance,
   and only the one you open costs a request. (user 2026-10-03: "have a date, then
   show its number. thru collapsible one?")

   It lives INSIDE the event, on the edit page, rather than as a popup off the
   list — attendance is part of the event, not a separate thing to go and find.

   For an ONLINE session the figures are measured and there is nothing to press.
   For a PHYSICAL one nothing is measured, so marking who turned up is the only
   source and the buttons are there.
   ========================================================================= */
export default function EventAttendance({ eventId, sessions = [], isOnline }) {
    const [openId, setOpenId] = useState(null);
    const [rowsBySession, setRowsBySession] = useState({});
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState('');

    const load = useCallback(async (sessionId) => {
        setLoading(sessionId);
        setError('');
        try {
            const res = await getRegistrations(sessionId);
            setRowsBySession(prev => ({ ...prev, [sessionId]: res.data || [] }));
        } catch {
            setError('Could not load that session.');
        } finally {
            setLoading(null);
        }
    }, []);

    const toggle = (s) => {
        if (openId === s.eventSessionId) { setOpenId(null); return; }
        setOpenId(s.eventSessionId);
        if (!rowsBySession[s.eventSessionId]) load(s.eventSessionId);
    };

    // The person about to be taken off, held until the confirm comes back.
    const [removing, setRemoving] = useState(null);

    const mark = async (reg, status, sessionId) => {
        try {
            await decideRegistration({ eventRegistrationId: reg.eventRegistrationId, status });
            await load(sessionId);
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not update that.');
        }
    };

    if (!sessions.length) {
        return (
            <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#94A3B8' }}>
                No sessions yet — add a date and save, then attendance appears here.
            </Typography>
        );
    }

    return (
        <Box>
            {isOnline && (
                <Typography sx={{ fontFamily: PP, fontSize: '0.76rem', color: '#64748B', mb: 1.5 }}>
                    Measured from the session itself — watch time, how much of it was in front of
                    them, and how much of the video they got through. Not set by hand.
                </Typography>
            )}
            {error && (
                <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#DC2626', mb: 1 }}>{error}</Typography>
            )}

            <Stack spacing={0.75}>
                {sessions.map(s => {
                    const open = openId === s.eventSessionId;
                    const rows = rowsBySession[s.eventSessionId] || [];
                    const past = s.endsOn && new Date(s.endsOn).getTime() < Date.now();
                    return (
                        <Box key={s.eventSessionId}
                            sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
                            <Stack
                                direction="row" alignItems="center" spacing={1}
                                onClick={() => toggle(s)}
                                sx={{
                                    px: 1.5, py: 1, cursor: 'pointer',
                                    bgcolor: open ? '#F8FAFC' : '#fff',
                                    '&:hover': { bgcolor: '#F8FAFC' },
                                }}
                            >
                                {open ? <ExpandMoreIcon sx={{ fontSize: 18, color: '#64748B' }} />
                                    : <ChevronRightIcon sx={{ fontSize: 18, color: '#64748B' }} />}
                                <Typography sx={{ fontFamily: PP, fontSize: '0.84rem', fontWeight: 600, color: '#0F172A' }}>
                                    {fmtDate(s.startsOn)}
                                </Typography>
                                {s.topic && (
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.76rem', color: '#94A3B8' }}>
                                        {s.topic}
                                    </Typography>
                                )}
                                <Box sx={{ flex: 1 }} />
                                {past && (
                                    <Chip label="Done" size="small"
                                        sx={{ height: 19, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />
                                )}
                                <Chip
                                    label={`${s.joinedCount ?? 0} going`}
                                    size="small"
                                    sx={{
                                        height: 21, fontSize: '0.66rem', fontWeight: 700,
                                        bgcolor: (s.joinedCount ?? 0) > 0 ? '#DCFCE7' : '#F1F5F9',
                                        color: (s.joinedCount ?? 0) > 0 ? '#065F46' : '#64748B',
                                    }} />
                                {(s.attendedCount ?? 0) > 0 && (
                                    <Chip label={`${s.attendedCount} attended`} size="small"
                                        sx={{ height: 21, fontSize: '0.66rem', fontWeight: 700, bgcolor: '#DBEAFE', color: '#1E40AF' }} />
                                )}
                            </Stack>

                            <Collapse in={open} unmountOnExit>
                                <Box sx={{ borderTop: '1px solid #E2E8F0' }}>
                                    {loading === s.eventSessionId ? (
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#94A3B8', p: 2 }}>
                                            Loading…
                                        </Typography>
                                    ) : rows.length === 0 ? (
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#94A3B8', p: 2 }}>
                                            No registrations yet
                                        </Typography>
                                    ) : (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={headSx}>Agent</TableCell>
                                                    <TableCell sx={headSx}>Status</TableCell>
                                                    <TableCell sx={headSx}>Watched</TableCell>
                                                    <TableCell sx={headSx}>Active screen</TableCell>
                                                    <TableCell sx={headSx}>Complete</TableCell>
                                                    <TableCell sx={headSx} align="right" />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rows.map(r => (
                                                    <TableRow key={r.eventRegistrationId}>
                                                        <TableCell sx={cellSx}>{r.agentName}</TableCell>
                                                        <TableCell>
                                                            <Chip label={r.status} size="small"
                                                                sx={{ height: 19, fontSize: '0.62rem', fontWeight: 700 }} />
                                                        </TableCell>
                                                        <TableCell sx={cellSx}>
                                                            {r.watchedSeconds > 0 ? `${Math.floor(r.watchedSeconds / 60)} min` : '—'}
                                                        </TableCell>
                                                        <TableCell sx={cellSx}>
                                                            {r.watchedSeconds > 0
                                                                ? `${Math.round(100 * r.focusedSeconds / r.watchedSeconds)}%` : '—'}
                                                        </TableCell>
                                                        <TableCell sx={cellSx}>
                                                            {r.completionPercent ? `${r.completionPercent}%` : '—'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                                {r.status === 'Requested' ? (
                                                                    <>
                                                                        <Button size="small" onClick={() => mark(r, 'Joined', s.eventSessionId)}
                                                                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.72rem' }}>
                                                                            Approve
                                                                        </Button>
                                                                        <Button size="small" onClick={() => mark(r, 'Declined', s.eventSessionId)}
                                                                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.72rem', color: '#DC2626' }}>
                                                                            Decline
                                                                        </Button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {isOnline ? (
                                                                            <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8' }}>
                                                                                measured
                                                                            </Typography>
                                                                        ) : (
                                                                            <>
                                                                                <Button size="small" onClick={() => mark(r, 'Attended', s.eventSessionId)}
                                                                                    sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.72rem' }}>
                                                                                    Attended
                                                                                </Button>
                                                                                <Button size="small" onClick={() => mark(r, 'NoShow', s.eventSessionId)}
                                                                                    sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.72rem', color: '#92400E' }}>
                                                                                    No-show
                                                                                </Button>
                                                                            </>
                                                                        )}
                                                                        {/* Somebody booked by mistake, or asked to be taken off. Without this
                                                                            an organiser could not undo a booking at all, and on a capped
                                                                            session the seat stayed locked to them. (user 2026-10-04) */}
                                                                        {r.status !== 'Cancelled' && r.status !== 'Attended' && (
                                                                            <Button size="small"
                                                                                onClick={() => setRemoving({ reg: r, sessionId: s.eventSessionId })}
                                                                                sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.72rem', color: '#DC2626' }}>
                                                                                Remove
                                                                            </Button>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </Box>
                            </Collapse>
                        </Box>
                    );
                })}
            </Stack>

            <Dialog open={!!removing} onClose={() => setRemoving(null)}>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1rem' }}>
                    Remove from session
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.86rem', color: '#475569' }}>
                        {removing?.reg?.agentName || 'This person'} loses their place. Anything already
                        measured is kept, and they can book again themselves while there is room.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoving(null)}
                        sx={{ fontFamily: PP, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error"
                        onClick={() => {
                            const { reg, sessionId } = removing;
                            setRemoving(null);
                            mark(reg, 'Cancelled', sessionId);
                        }}
                        sx={{ fontFamily: PP, textTransform: 'none' }}>
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
